<template>
  <form class="company-modal__form">
    <!-- START: Customer Data Section -->
    <div class="row gutter-lg items-start">
      <div class="col-sm-auto col-12 gt-xs">
        <div class="bg-grey-1 circle text-center q-mr-lg" style="height: 125px; width: 125px; line-height: 125px;">
          <q-icon name="business" color="grey-3" size="100px" class="bg-grey-1 circle"/>
        </div>
      </div>

      <div class="col-sm col-12">
        <!-- Name -->
        <div class="row">
          <q-field :error="errors.has('companyName')" :error-label="errors.first('companyName')" class="col-12">
            <q-input
              ref="companyName"
              v-model="formData.name"
              v-validate.disable="'required|min:1|max:255'"
              name="companyName"
              :placeholder="$t('company_name')"
              required
            />
          </q-field>
        </div>

        <!-- business partner type -->
        <div v-if="!restrictToBusinessPartnerType" class="row">
          <q-field :error="errors.has('businessPartnerType')" :error-label="errors.first('businessPartnerType')" class="col-12">
            <q-select
              v-model="formData.businessPartnerType"
              :options="businessPartnerTypeOptions"
              :placeholder="$t('business_partner_type')"
              v-validate.disable="'required'"
              name="businessPartnerType"
              required
            />
          </q-field>
        </div>

        <template v-if="showAllFields">

          <div class="row gutter-x-sm">
            <!-- VAT Number -->
            <q-field
              class="col-12 col-md-6"
              icon="account_balance"
              :error="errors.has('vat_number')"
              :error-label="errors.first('vat_number')"
            >
              <q-input
                v-model="formData.vatNumber"
                v-validate.disable="'min:3|max:255'"
                name="vatNumber"
                :placeholder="$t('vat_number')"
              />
            </q-field>

            <!-- Tax Number -->
            <q-field
              class="col-12 col-md-6"
              :error="errors.has('tax_number')"
              :error-label="errors.first('tax_number')"
            >
              <q-input
                v-model="formData.taxNumber"
                v-validate.disable="'min:3|max:255'"
                name="vatNumber"
                :placeholder="$t('tax_number')"
              />
            </q-field>
          </div>
        </template>

        <!-- Default Payment Deadline Input -->
        <div v-if="showAllFields" class="row">
          <q-field
            class="col-12"
            icon="calendar_today"
            :error="errors.has('defaultPaymentDeadline')"
            :error-label="errors.first('defaultPaymentDeadline')"
          >
            <q-input
              v-model="formData.defaultPaymentDeadline"
              v-validate.disable="'numeric|min:0'"
              name="defaultPaymentDeadline"
              class="col"
              type="number"
              :placeholder="$t('payment_deadline')"
              :data-vv-as="$t('payment_deadline')"
              :suffix="$tc('day', formData.defaultPaymentDeadline)"/>
          </q-field>
        </div>

        <!-- Customer Number Input -->
        <div v-show="!generateCustomerNumber || isEditing" class="row">
          <q-field
            class="col-12"
            icon="account_circle"
            :error="errors.has('customerNumber')"
            :error-label="errors.first('customerNumber')"
          >
            <q-input
              v-model="formData.customerNumber"
              v-validate.disable="'max:45'"
              name="customerNumber"
              :placeholder="$t('person_customer_number')"
              class="col"/>
          </q-field>
        </div>

        <!-- Customer Number Checkbox -->
        <div v-if="!isEditing" class="row">
          <q-field>
            <q-checkbox
              v-model="generateCustomerNumber"
              :label="$t('auto_generate_customer_number')"
            />
          </q-field>
        </div>
      </div>
    </div>
    <!-- END: Customer Data Section -->

    <!-- START: Contact Data Section -->
    <div class="row q-mt-md">
      <div class="col-12">
        <multi-address-input
          v-model="formData.addresses"
          :search-hint="formData.name"
          hide-label
          show-icon
          label
        />
      </div>
    </div>

    <div class="row q-mt-md">
      <div class="col-12">
         <multi-bank-details-input
            v-model="formData.bankDetails"
            hide-label
            show-icon
            label
          />
      </div>
    </div>

    <template v-if="showAllFields">
      <div class="row q-mt-md">
        <div class="col-12">
          <multi-email-input
            v-model="formData.emailAddresses"
            hide-label
            show-icon
            label
          />
        </div>
      </div>

      <div class="row q-mt-md">
        <div class="col-12">
          <multi-phone-input
            v-model="formData.phoneNumbers"
            hide-label
            show-icon
            label
          />
        </div>
      </div>

      <!-- Website -->
      <div class="row q-mt-md">
        <q-field
          class="col-12"
          icon="public"
          :error="errors.has('website')"
          :error-label="errors.first('website')"
        >
          <q-input
            v-model="formData.website"
            v-validate.disable="'min:3|max:255'"
            name="website"
            :placeholder="$t('company_website')"
          />
        </q-field>
      </div>

      <div class="row q-mt-md">
        <q-field
          class="col-12"
          icon="account_circle"
          :error="errors.has('accountsReceivableNumber')"
          :error-label="errors.first('accountsReceivableNumber')"
        >
          <q-input
            v-model="formData.accountsReceivableNumber"
            v-validate.disable="'max:45'"
            name="accountsReceivableNumber"
            :placeholder="$t('accounts_receivable_number')"
            class="col"
            clearable
          />
        </q-field>
      </div>

      <div class="row q-mt-md">
        <q-field
          class="col-12"
          icon="account_circle"
          :error="errors.has('accountsPayableNumber')"
          :error-label="errors.first('accountsPayableNumber')"
        >
          <q-input
            v-model="formData.accountsPayableNumber"
            v-validate.disable="'max:45'"
            name="accountsPayableNumber"
            :placeholder="$t('accounts_payable_number')"
            class="col"
            clearable
          />
        </q-field>
      </div>
    </template>
    <!-- END: Contact Data Section -->

    <div class="q-mt-md">
      <q-btn
        flat
        :icon="showAllFields ? 'expand_less' : 'expand_more'"
        color="primary"
        class="q-px-none"
        @click="showAllFields = !showAllFields"
      >
        {{ showAllFields ? $t('show_less') : $t('show_more') }}
      </q-btn>
    </div>
  </form>
</template>

<script>
  import { businessPartnerTypes, businessPartnerTypesSelectOptions } from 'src/utils/businessPartnerTypes'
  import { mapActions, mapGetters } from 'vuex'
  import AddressSubResource from 'src/utils/SubResources/CustomerAddress'
  import BankDetailsSubResource from 'src/utils/SubResources/CustomerBankDetails'
  import EmailSubResource from 'src/utils/SubResources/CustomerEmailAddress'
  import MultiAddressInput from 'components/Common/Addressing/MultiAddressInput.vue'
  import MultiBankDetailsInput from 'components/Common/Addressing/MultiBankDetailsInput.vue'
  import MultiEmailInput from 'components/Common/Addressing/MultiEmailInput.vue'
  import MultiPhoneInput from 'components/Common/Addressing/MultiPhoneInput.vue'
  import PhoneSubResource from 'src/utils/SubResources/CustomerPhoneNumber'

  function initialState (props) {
    return {
      customerNumber: '',
      businessPartnerType: props.restrictToBusinessPartnerType ? props.restrictToBusinessPartnerType : businessPartnerTypes.CUSTOMER,
      name: '',
      industry: '',
      website: '',
      vatNumber: '',
      taxNumber: '',
      defaultPaymentDeadline: null,
      accountsReceivableNumber: null,
      accountsPayableNumber: null,
      emailAddresses: [
        EmailSubResource.empty()
      ],
      phoneNumbers: [
        PhoneSubResource.empty()
      ],
      addresses: [
        AddressSubResource.empty()
      ],
      bankDetails: [
        BankDetailsSubResource.empty()
      ]
    }
  }

  export default {
    inject: ['$validator'],

    props: {
      action: {
        required: true,
        type: String
      },
      restrictToBusinessPartnerType: {
        required: false,
        type: String,
        default: null,
        valdiator: (value) => businessPartnerTypes.values().includes(value)
      }
    },

    data () {
      return {
        formData: initialState(this.$props),
        showAllFields: false
      }
    },

    computed: {
      ...mapGetters(['autoGenerateCustomerNumber']),

      isEditing () {
        return this.action === 'update'
      },

      generateCustomerNumber: {
        get () {
          return this.autoGenerateCustomerNumber
        },

        set (value) {
          this.setAutoGenerateCustomerNumber(value)
        }
      },

      businessPartnerTypeOptions () {
        return businessPartnerTypesSelectOptions
      },

      businessPartnerType () {
        return this.formData.businessPartnerType
      }
    },

    methods: {
      ...mapActions(['setAutoGenerateCustomerNumber']),

      handleShowMore () {
        this.showAllFields = !this.showAllFields
      },

      resetData () {
        this.formData = initialState(this.$props)
        this.showAllFields = false
      },

      loadData (company) {
        this.formData = Object.assign(initialState(this.$props), company)

        this.showAllFields = true
      },

      focus () {
        this.$refs.companyName.focus()
      }
    },

    watch: {
      formData: {
        handler: function (newVal) {
          this.$emit('input', newVal)
        },
        deep: true
      }
    },

    components: {
      MultiAddressInput,
      MultiEmailInput,
      MultiPhoneInput,
      MultiBankDetailsInput
    }
  }
</script>
